import { useState, useEffect, useCallback, useRef } from 'react';
import { motion } from 'framer-motion';
import styles from './Testimonials.module.css';

const REVIEWS = [
  {
    quote: 'The makeover was absolutely flawless and tailored perfectly to my features and style. Highly professional.',
    name: 'Baby Inaaya',
  },
  {
    quote: "I'm very happy because you made my special day more special. Makeup was amazing. Shobha ma'am thank you so much.",
    name: 'Shivangi Pandey',
  },
  {
    quote: 'Very cost efficient and effective. Shobha ma\'am guided the best for glowing makeup. Perfect bridal makeup artist in Bharatpur.',
    name: 'Sunita',
  },
  {
    quote: 'There is some magic in your hands. Beauty and confidence become more magnetic when you do the makeup.',
    name: 'Shivangi Sharma',
  },
  {
    quote: 'Staff is very polite and she is very professional. Visit once — you will be satisfied.',
    name: 'Chetna Sharma',
  },
  {
    quote: 'My engagement makeup was done by Shobha mam. Very professional and very friendly. They understood all my expectations.',
    name: 'Brajendar Singh',
  },
  {
    quote: 'The look was flawless, subtle and exactly how I wanted. The team was very polite and cooperative.',
    name: 'Priyanka Kumari',
  },
];

// Show 3 cards per page on desktop
const PAGE_SIZE = 3;
const TOTAL_PAGES = Math.ceil(REVIEWS.length / PAGE_SIZE);

function ReviewCard({ quote, name, delay }) {
  return (
    <motion.div
      className={styles.card}
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.2 }}
      transition={{ duration: 0.65, delay }}
    >
      <div className={styles.quoteIcon} aria-hidden="true">"</div>
      <p className={styles.quote}>{quote}</p>
      <div className={styles.stars} aria-label="5 stars">★★★★★</div>
      <div className={styles.reviewerName}>— {name}</div>
      <span className={styles.badge}>✓ Google Review</span>
    </motion.div>
  );
}

export default function Testimonials() {
  const [page, setPage] = useState(0);
  const [paused, setPaused] = useState(false);
  const timerRef = useRef(null);

  const next = useCallback(() => {
    setPage((p) => (p + 1) % TOTAL_PAGES);
  }, []);

  const prev = useCallback(() => {
    setPage((p) => (p - 1 + TOTAL_PAGES) % TOTAL_PAGES);
  }, []);

  // Auto-rotate every 5s unless paused
  useEffect(() => {
    if (paused) return;
    timerRef.current = setTimeout(next, 5000);
    return () => clearTimeout(timerRef.current);
  }, [page, paused, next]);

  const visible = REVIEWS.slice(page * PAGE_SIZE, page * PAGE_SIZE + PAGE_SIZE);

  return (
    <section id="reviews" className={styles.testimonials}>
      <div className={styles.inner}>
        {/* Heading */}
        <motion.div
          className={styles.heading}
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.5 }}
          transition={{ duration: 0.7 }}
        >
          <div className={styles.headingTag}>Client Reviews</div>
          <h2 className={styles.headingTitle}>What Our Clients Say</h2>
          <p className={styles.headingSub}>
            4.8★ across hundreds of reviews on Google &amp; Justdial
          </p>
        </motion.div>

        {/* Cards */}
        <div
          className={styles.carouselWrap}
          onMouseEnter={() => setPaused(true)}
          onMouseLeave={() => setPaused(false)}
        >
          <div className={styles.slides}>
            {visible.map((r, i) => (
              <ReviewCard key={r.name} quote={r.quote} name={r.name} delay={i * 0.08} />
            ))}
          </div>

          {/* Controls */}
          <div className={styles.controls}>
            <button
              className={styles.controlBtn}
              onClick={prev}
              aria-label="Previous reviews"
            >
              ‹
            </button>

            <div className={styles.dots} role="tablist">
              {Array.from({ length: TOTAL_PAGES }).map((_, i) => (
                <button
                  key={i}
                  className={`${styles.dot} ${i === page ? styles.dotActive : ''}`}
                  onClick={() => setPage(i)}
                  aria-label={`Go to page ${i + 1}`}
                  role="tab"
                  aria-selected={i === page}
                />
              ))}
            </div>

            <button
              className={styles.controlBtn}
              onClick={next}
              aria-label="Next reviews"
            >
              ›
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}
